/* ============================================================
   BNE STUDIO — Tools (Midnight Blueprint)
   Recreated from the design handoff:
   "BNE Studio - Tools.html". Renders inside NightLayout
   (shared nav/footer + motion engine + scoped design system).
   Page-specific CSS lives in night-tools.css (scoped to .night).
   Cross-links use react-router. The page lives at /tools.
   ============================================================ */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";
import "../styles/night-tools.css";

const STEM_AND_MIX_URL = "https://bne.to/ols";
const SMART_LINKS_URL = "https://bne.to";
const LYRICS_URL = "https://bne.to/lyrics";
const MUSIC_SUITE_URL = "https://apps.apple.com/us/app/music-suite-bpm-pitch-shift/id6475732700";

export const ToolsPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "BNE Tools — For the work around the song";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout description="Split any track, perfect any mix, and turn one link into a fanbase." active="tools">
      {/* HERO */}
      <header className="phero" style={{ "--c": "var(--p-tools)" }}>
        <div className="wrap">
          <div style={{ maxWidth: "760px" }} className="reveal">
            <div className="ico-badge">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M5 12l4-4M5 12l4 4M19 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="eyebrow" style={{ marginBottom: "16px" }}>BNE Tools</span>
            <h1 className="grad-text">Superpowers for every song.</h1>
            <p className="lead" style={{ maxWidth: "640px" }}>Split any track, perfect any mix, and turn one link into a fanbase.</p>
          </div>
        </div>
      </header>

      {/* FEATURED */}
      <section className="section tight">
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Featured tools</span>
            <h2>The heavy lifters.</h2>
          </div>
          <div className="ft-grid">
            {/* Stem Separator */}
            <div className="ft reveal" id="stem-separator" data-d="1" style={{ "--c": "var(--p-tools)" }}>
              <div className="ti">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 2 2 7l10 5 10-5-10-5ZM2 12l10 5 10-5M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Stem Separator</h3>
              <p>Split a finished track into vocals, drums, bass, and instruments — ready to practice, remix, review, or repurpose.</p>
              <div className="imagery">
                <div className="stem-lane">
                  <span className="nm">Vocals</span>
                  <span className="wv">
                    <i style={{ height: "8px" }} />
                    <i style={{ height: "16px" }} />
                    <i style={{ height: "11px" }} />
                    <i style={{ height: "18px" }} />
                    <i style={{ height: "7px" }} />
                    <i style={{ height: "14px" }} />
                    <i style={{ height: "10px" }} />
                    <i style={{ height: "17px" }} />
                    <i style={{ height: "9px" }} />
                    <i style={{ height: "13px" }} />
                  </span>
                </div>
                <div className="stem-lane">
                  <span className="nm">Drums</span>
                  <span className="wv">
                    <i style={{ height: "14px" }} />
                    <i style={{ height: "6px" }} />
                    <i style={{ height: "16px" }} />
                    <i style={{ height: "8px" }} />
                    <i style={{ height: "17px" }} />
                    <i style={{ height: "7px" }} />
                    <i style={{ height: "15px" }} />
                    <i style={{ height: "9px" }} />
                    <i style={{ height: "14px" }} />
                    <i style={{ height: "6px" }} />
                  </span>
                </div>
                <div className="stem-lane">
                  <span className="nm">Bass</span>
                  <span className="wv">
                    <i style={{ height: "10px" }} />
                    <i style={{ height: "12px" }} />
                    <i style={{ height: "9px" }} />
                    <i style={{ height: "13px" }} />
                    <i style={{ height: "11px" }} />
                    <i style={{ height: "14px" }} />
                    <i style={{ height: "8px" }} />
                    <i style={{ height: "12px" }} />
                    <i style={{ height: "10px" }} />
                    <i style={{ height: "13px" }} />
                  </span>
                </div>
                <div className="stem-lane">
                  <span className="nm">Inst.</span>
                  <span className="wv">
                    <i style={{ height: "12px" }} />
                    <i style={{ height: "9px" }} />
                    <i style={{ height: "15px" }} />
                    <i style={{ height: "7px" }} />
                    <i style={{ height: "13px" }} />
                    <i style={{ height: "10px" }} />
                    <i style={{ height: "16px" }} />
                    <i style={{ height: "8px" }} />
                    <i style={{ height: "12px" }} />
                    <i style={{ height: "9px" }} />
                  </span>
                </div>
              </div>
              <div className="cta-row">
                <a className="btn btn-primary" href={STEM_AND_MIX_URL}>Separate Stems</a>
                <span className="dom">bne.to/ols</span>
              </div>
            </div>
            {/* Mix Feedback */}
            <div className="ft reveal" id="mix-feedback" data-d="2" style={{ "--c": "var(--p-tools)" }}>
              <div className="ti">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M21 11.5a8.4 8.4 0 0 1-12.8 7.2L3 21l2.3-5.2A8.4 8.4 0 1 1 21 11.5ZM9 11v2M12 9v6M15 11v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Mix Feedback</h3>
              <p>Review balance, clarity, loudness, and release-readiness with feedback that helps you make better mix decisions.</p>
              <div className="imagery">
                <div className="score-ring">
                  <div className="ring"><b>78</b></div>
                  <div className="bars">
                    <div className="b">
                      <span style={{ width: "46px" }}>Balance</span>
                      <span className="t"><span style={{ width: "82%" }} /></span>
                    </div>
                    <div className="b">
                      <span style={{ width: "46px" }}>Clarity</span>
                      <span className="t"><span style={{ width: "74%" }} /></span>
                    </div>
                    <div className="b">
                      <span style={{ width: "46px" }}>Loudness</span>
                      <span className="t"><span style={{ width: "68%" }} /></span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="cta-row">
                <a className="btn btn-primary" href={STEM_AND_MIX_URL}>Check a Mix</a>
                <span className="dom">bne.to/ols</span>
              </div>
            </div>
            {/* Smart Links */}
            <div className="ft reveal" id="smart-links" data-d="1" style={{ "--c": "var(--p-links)" }}>
              <div className="ti">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 15l6-6M10 6l1-1a4 4 0 016 6l-1 1M14 18l-1 1a4 4 0 01-6-6l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Smart Links</h3>
              <p>One link for every campaign — releases, videos, tickets, and creator pages, with clicks you can actually see.</p>
              <div className="imagery">
                <div className="sl">
                  <div className="u">bne.to/neon-hours</div>
                  <div className="b"><span>Spotify</span><span>→</span></div>
                  <div className="b"><span>Apple Music</span><span>→</span></div>
                  <div className="b"><span>YouTube</span><span>→</span></div>
                  <div className="b" style={{ borderTop: "1px solid var(--line-soft)", marginTop: "6px", paddingTop: "8px" }}>
                    <span style={{ color: "var(--fog)", fontFamily: "var(--mono)", fontSize: "10px" }}>2,140 clicks</span>
                    <span style={{ color: "var(--p-links)", fontFamily: "var(--mono)", fontSize: "10px" }}>↗ 18%</span>
                  </div>
                </div>
              </div>
              <div className="cta-row">
                <a className="btn btn-primary" href={SMART_LINKS_URL}>Build a Smart Link</a>
                <span className="dom">bne.to</span>
              </div>
            </div>
            {/* Music Suite */}
            <div className="ft reveal" id="music-suite" data-d="2" style={{ "--c": "var(--p-tools)" }}>
              <div className="ti">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M7 2h10a1 1 0 011 1v18a1 1 0 01-1 1H7a1 1 0 01-1-1V3a1 1 0 011-1zM10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Music Suite</h3>
              <p>A pocket toolkit for iPhone — adjust tempo, shift pitch, loop sections, and work with musical ideas on the move.</p>
              <div className="imagery">
                <div className="phone-mini">
                  <div className="notch" />
                  <div className="dial">124</div>
                  <div className="keys">
                    <i style={{ height: "10px" }} />
                    <i style={{ height: "18px" }} />
                    <i style={{ height: "8px" }} />
                    <i style={{ height: "15px" }} />
                    <i style={{ height: "20px" }} />
                    <i style={{ height: "11px" }} />
                    <i style={{ height: "16px" }} />
                    <i style={{ height: "9px" }} />
                  </div>
                </div>
              </div>
              <div className="cta-row">
                <a className="btn btn-primary" href={MUSIC_SUITE_URL}>Download Music Suite</a>
                <span className="dom">App Store</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="section tight">
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Community tool</span>
            <h2>Made better together.</h2>
          </div>
          <a className="t-community reveal" id="lyrics" data-d="1" href={LYRICS_URL}>
            <div className="ti">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: "20px", letterSpacing: "-0.02em", marginBottom: "6px", color: "var(--heading)" }}>Lyrics</h4>
              <p style={{ fontSize: "14px", color: "var(--pebble)", lineHeight: "1.5", maxWidth: "58ch" }}>Community-supported lyric submissions so songs are easier to read, share, and understand. Submit, improve, and review with section markers and credits.</p>
            </div>
            <span className="btn btn-ghost" style={{ "--c": "var(--p-lyrics)" }}>Submit Lyrics</span>
          </a>
        </div>
      </section>

      {/* AUDIO HELPERS */}
      <section className="section tight">
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Audio helpers</span>
            <h2>Quick utilities for the small stuff.</h2>
          </div>
          <div className="helper-row">
            <a className="helper reveal" id="bpm-finder" data-d="1" style={{ "--c": "var(--p-tools)" }} href={MUSIC_SUITE_URL}>
              <span className="hi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3 4 19h16L12 3ZM12 11v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div><b>BPM Finder</b><span>Find the tempo fast</span></div>
            </a>
            <a className="helper reveal" id="pitch-shifter" data-d="2" style={{ "--c": "var(--p-tools)" }} href={MUSIC_SUITE_URL}>
              <span className="hi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 14h3l3-8 4 16 3-8h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div><b>Pitch Shifter</b><span>Shift to a new key</span></div>
            </a>
            <a className="helper reveal" id="tap-tempo" data-d="3" style={{ "--c": "var(--p-tools)" }} href={MUSIC_SUITE_URL}>
              <span className="hi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 11l3 3L22 4M21 12a9 9 0 11-6.2-8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div><b>Tap Tempo</b><span>Tap the beat, get BPM</span></div>
            </a>
          </div>
        </div>
      </section>

      {/* CROSS-PROMOTE */}
      <section className="section" style={{ "--c": "var(--p-tools)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Keep the rollout moving</span>
            <h2>Done with a tool? Here's what's next.</h2>
          </div>
          <div className="eco-grid">
            <Link className="eco reveal" data-d="1" style={{ "--c": "var(--p-sync)" }} to="/sync">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6a2 2 0 012-2h4l2 2.5h6a2 2 0 012 2V18a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Sync</b>
              <p>Organize your stems in a project.</p>
            </Link>
            <Link className="eco reveal" data-d="2" style={{ "--c": "var(--p-dist)" }} to="/distribute">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Distribution</b>
              <p>Release when the mix is ready.</p>
            </Link>
            <Link className="eco reveal" data-d="3" style={{ "--c": "var(--p-vision)" }} to="/vision">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3ZM3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Vision</b>
              <p>Turn the song into a reel.</p>
            </Link>
            <Link className="eco reveal" data-d="4" style={{ "--c": "var(--p-live)" }} to="/live">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2 2 2 0 000 4 2 2 0 000 4 2 2 0 01-2 2H6a2 2 0 01-2-2 2 2 0 000-4 2 2 0 000-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Live</b>
              <p>Put a show on sale.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section tight">
        <div className="wrap">
          <div className="cta-band reveal-scale">
            <div className="glow" />
            <span className="eyebrow center" style={{ justifyContent: "center", marginBottom: "20px" }}>BNE Tools</span>
            <h2 className="grad-text">Reach for the right tool.</h2>
            <p>Everything around the song — stems, mixes, links, lyrics, and quick helpers.</p>
            <div className="row" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <a className="btn btn-primary btn-lg btn-arrow" href="#stem-separator">
                Explore the Tools <svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </a>
              <Link className="btn btn-ghost btn-lg" to="/home">Back to the Ecosystem</Link>
            </div>
          </div>
        </div>
      </section>
    </NightLayout>
  );
};

export default ToolsPage;
